import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent } from '../../../components/ui/card';
import {
  ChevronLeft, ChevronRight, Loader2, Send, CopyPlus, CalendarRange, AlertTriangle, Clock,
} from 'lucide-react';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';
import WeekGrid from './WeekGrid';
import DayList from './DayList';
import BookingDialog from './BookingDialog';
import ShiftDialog from './ShiftDialog';
import CopyWeekDialog from './CopyWeekDialog';
import CopyHoursDialog from './CopyHoursDialog';
import HoursRunDialog from './HoursRunDialog';
import ImpactPreviewDialog from './ImpactPreviewDialog';
import {
  addDays, fmtDate, hoursLabel, money, todayStr, weekStartOf,
} from '../../../lib/scheduleFormat';

const Builder = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [weekStart, setWeekStart] = useState(() =>
    weekStartOf(searchParams.get('week') || searchParams.get('date') || todayStr())
  );
  const [week, setWeek] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [bookingDialog, setBookingDialog] = useState(null); // {day, booking}
  const [shiftDialog, setShiftDialog] = useState(null);     // {day, row, shift}
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyResult, setCopyResult] = useState(null);
  const [hoursCopyOpen, setHoursCopyOpen] = useState(false);
  const [hoursPlan, setHoursPlan] = useState(null);
  const [usualRun, setUsualRun] = useState(null);             // {date, kitchenId, run}
  const [impact, setImpact] = useState(null);               // {payload, onConfirm}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [weekData, staffData] = await Promise.all([
        adminService.getScheduleWeek(weekStart),
        adminService.getSchedulingStaff(),
      ]);
      setWeek(weekData);
      setStaff(staffData);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load the schedule');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  const goto = (next) => {
    setWeekStart(next);
    setSearchParams({ week: next });
  };

  const dayFor = (dateStr) => week?.days.find((d) => d.date === dateStr);

  // ---------------- bookings ----------------
  const saveBooking = async (payload) => {
    const { booking } = bookingDialog;
    setBusy(true);
    try {
      if (booking) {
        // Editing hours can strand the shifts inside them — preview first.
        const preview = await adminService.previewBookingImpact({
          booking_id: booking.id, action: 'edit', ...payload,
        }).catch(() => ({ has_impact: false }));
        if (preview.has_impact) {
          setImpact({
            impact: preview,
            title: 'Changing these hours strands scheduled shifts',
            confirmLabel: 'Change anyway',
            run: async () => {
              await adminService.updateKitchenBooking(booking.id, payload);
              toast.success('Booking updated — affected shifts are flagged');
            },
          });
          setBusy(false);
          return;
        }
        await adminService.updateKitchenBooking(booking.id, payload);
        toast.success('Booking updated');
      } else {
        await adminService.createKitchenBooking(payload);
        toast.success('Kitchen hours booked');
      }
      setBookingDialog(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the booking');
    } finally {
      setBusy(false);
    }
  };

  const deleteBooking = async (booking) => {
    setBusy(true);
    try {
      const preview = await adminService.previewBookingImpact({
        booking_id: booking.id, action: 'cancel',
      }).catch(() => ({ has_impact: false }));
      if (preview.has_impact) {
        setImpact({
          impact: preview,
          title: 'Cancelling this booking leaves shifts without kitchen hours',
          confirmLabel: 'Cancel the booking',
          run: async () => {
            const result = await adminService.deleteKitchenBooking(booking.id);
            toast.success(`Booking cancelled — ${result.shifts_flagged} shift(s) flagged for you to resolve`);
          },
        });
        setBusy(false);
        return;
      }
      await adminService.deleteKitchenBooking(booking.id);
      toast.success('Booking cancelled');
      setBookingDialog(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel the booking');
    } finally {
      setBusy(false);
    }
  };

  // ---------------- shifts ----------------
  const saveShift = async (payload) => {
    setBusy(true);
    try {
      if (shiftDialog.shift) {
        await adminService.updateShift(shiftDialog.shift.id, payload);
        toast.success('Shift updated');
      } else {
        await adminService.createShift(payload);
        toast.success('Shift added');
      }
      setShiftDialog(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the shift');
    } finally {
      setBusy(false);
    }
  };

  const deleteShift = async (shift) => {
    setBusy(true);
    try {
      await adminService.deleteShift(shift.id);
      toast.success('Shift removed');
      setShiftDialog(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove the shift');
    } finally {
      setBusy(false);
    }
  };

  // ---------------- publish / copy ----------------
  const publish = async () => {
    setBusy(true);
    try {
      const result = await adminService.publishScheduleWeek(weekStart);
      toast.success(
        `Published ${result.published_shifts} shift(s) — ${result.people_notified} person(s) notified`
      );
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not publish the week');
    } finally {
      setBusy(false);
    }
  };

  const copyWeek = async (source, options = {}) => {
    setBusy(true);
    try {
      const result = await adminService.copyScheduleWeek(source, weekStart, options);
      setCopyResult(result);
      if (result.totals?.bookings > 0) {
        toast.success(`${result.totals.bookings} day(s) of kitchen hours booked`);
      }
      if (result.created > 0) toast.success(`${result.created} shift(s) copied as drafts`);
      if (result.skipped?.length) toast.warning(`${result.skipped.length} did not fit the new week`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not copy that week');
    } finally {
      setBusy(false);
    }
  };

  const confirmImpact = async () => {
    setBusy(true);
    try {
      await impact.run();
      setImpact(null);
      setBookingDialog(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not apply the change');
    } finally {
      setBusy(false);
    }
  };

  // ---------------- hours ahead / one day's usual hours ----------------
  const kitchenFor = (dateStr) =>
    (week?.kitchens || []).find(
      (k) => k.active_from <= dateStr && (!k.active_to || k.active_to >= dateStr)
    ) || week?.kitchens?.[0];

  const runHoursCopy = async (source, weeks, commit) => {
    setBusy(true);
    try {
      const result = await adminService.copyHoursIntoWeeks(source, weeks, commit);
      setHoursPlan(result);
      if (commit) {
        toast.success(`${result.totals.bookings} day(s) of kitchen hours booked`);
        await load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not copy those hours');
    } finally {
      setBusy(false);
    }
  };

  const usualHours = async (day, commit = false) => {
    const kitchen = kitchenFor(day.date);
    if (!kitchen) return;
    setBusy(true);
    try {
      const run = await adminService.hoursPatternForDay(kitchen.id, day.date, commit);
      setUsualRun({ date: day.date, kitchenId: kitchen.id, run });
      if (commit) {
        toast.success(run.totals.bookings > 0
          ? `${day.date} booked · ${hoursLabel(run.totals.hours)}`
          : 'Nothing could be booked on that day');
        await load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not read the standing hours');
    } finally {
      setBusy(false);
    }
  };

  const eligibleStaff = useMemo(() => staff.filter((person) => person.active), [staff]);

  if (loading && !week) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="builder-loading">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="schedule-builder">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Schedule builder</h1>
          <p className="mt-1 text-sm text-gray-600">
            Kitchen access, the hours you book inside it, and who works them — all in bakery local time
            ({week?.timezone}).
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <Button variant="outline" className="flex-1 sm:flex-none" onClick={() => setCopyOpen(true)} data-testid="builder-copy-week">
            <CopyPlus className="mr-1 h-4 w-4" /> Copy a week
          </Button>
          <Button
            variant="outline" className="flex-1 sm:flex-none"
            onClick={() => { setHoursPlan(null); setHoursCopyOpen(true); }}
            data-testid="builder-copy-hours"
          >
            <CalendarRange className="mr-1 h-4 w-4" /> Copy hours ahead
          </Button>
          <Button
            onClick={publish}
            disabled={busy || !week?.can_publish}
            className="flex-1 bg-amber-600 hover:bg-amber-700 sm:flex-none"
            data-testid="builder-publish"
          >
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
            Publish week
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <Button variant="outline" size="icon" onClick={() => goto(addDays(weekStart, -7))}
              aria-label="Previous week" data-testid="builder-prev-week">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0 flex-1 text-center sm:min-w-[13rem] sm:flex-none">
              <p className="font-semibold text-gray-900" data-testid="builder-week-label">
                {fmtDate(weekStart)} – {fmtDate(addDays(weekStart, 6))}
              </p>
              <p className="text-xs text-gray-500">
                {week?.status === 'published' ? 'Published' : week?.status === 'draft' ? 'Draft — staff cannot see it' : 'Nothing scheduled'}
              </p>
            </div>
            <Button variant="outline" size="icon" onClick={() => goto(addDays(weekStart, 7))}
              aria-label="Next week" data-testid="builder-next-week">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => goto(weekStartOf(todayStr()))}
              data-testid="builder-this-week">
              <CalendarRange className="mr-1 h-4 w-4" /> This week
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="builder-totals">
            <Badge variant="outline" className="bg-white">
              {hoursLabel(week?.totals.booked_hours)} booked · {money(week?.totals.booked_cost)}
            </Badge>
            <Badge variant="outline" className="bg-white">{week?.totals.shifts} shifts</Badge>
            {week?.totals.draft > 0 && (
              <Badge variant="outline" className="border-slate-300 bg-slate-100 text-slate-700">
                {week.totals.draft} draft
              </Badge>
            )}
            {week?.totals.unconfirmed > 0 && (
              <Badge variant="outline" className="border-amber-300 bg-amber-100 text-amber-800">
                <Clock className="mr-1 h-3 w-3" /> {week.totals.unconfirmed} unconfirmed
              </Badge>
            )}
            {week?.totals.open_shifts > 0 && (
              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
                {week.totals.open_shifts} open
              </Badge>
            )}
            {week?.totals.conflicts > 0 && (
              <Badge variant="outline" className="border-red-300 bg-red-100 text-red-800">
                <AlertTriangle className="mr-1 h-3 w-3" /> {week.totals.conflicts} conflict
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {week?.kitchens.length === 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-900" data-testid="builder-no-kitchen">
            No kitchen is set up yet. Add the kitchen and its access agreement on the Kitchen Setup page
            before booking hours.
          </CardContent>
        </Card>
      )}

      {week && (
        <>
          <div className="hidden lg:block">
            <WeekGrid
              week={week}
              onAddShift={(day, row) => setShiftDialog({ day, row, shift: null })}
              onEditShift={(shift) => setShiftDialog({ day: dayFor(shift.date), row: null, shift })}
              onAddBooking={(day) => setBookingDialog({ day, booking: null })}
              onEditBooking={(booking, day) => setBookingDialog({ day, booking })}
              onUsualHours={(day) => usualHours(day, false)}
            />
          </div>
          <div className="lg:hidden">
            <DayList
              week={week}
              onAddShift={(day, row) => setShiftDialog({ day, row, shift: null })}
              onEditShift={(shift) => setShiftDialog({ day: dayFor(shift.date), row: null, shift })}
              onAddBooking={(day) => setBookingDialog({ day, booking: null })}
              onEditBooking={(booking, day) => setBookingDialog({ day, booking })}
              onUsualHours={(day) => usualHours(day, false)}
            />
          </div>
        </>
      )}

      {bookingDialog && (
        <BookingDialog
          open
          kitchens={week.kitchens}
          booking={bookingDialog.booking}
          date={bookingDialog.day?.date}
          availability={bookingDialog.day?.availability}
          busy={busy}
          onClose={() => setBookingDialog(null)}
          onSubmit={saveBooking}
          onDelete={deleteBooking}
        />
      )}

      {shiftDialog && (
        <ShiftDialog
          open
          shift={shiftDialog.shift}
          date={shiftDialog.day?.date}
          positions={week.positions}
          staff={eligibleStaff}
          bookings={shiftDialog.day?.bookings || []}
          availability={shiftDialog.day?.availability || []}
          presetUserId={shiftDialog.row?.user_id}
          presetPosition={shiftDialog.row?.positions?.[0]}
          busy={busy}
          onClose={() => setShiftDialog(null)}
          onSubmit={saveShift}
          onDelete={deleteShift}
        />
      )}

      {copyOpen && (
        <CopyWeekDialog
          open
          weekStart={weekStart}
          busy={busy}
          result={copyResult}
          onCopy={copyWeek}
          onClose={() => { setCopyOpen(false); setCopyResult(null); }}
        />
      )}

      {hoursCopyOpen && (
        <CopyHoursDialog
          open
          weekStart={weekStart}
          busy={busy}
          plan={hoursPlan}
          onPreview={(source, weeks) => runHoursCopy(source, weeks, false)}
          onCommit={(source, weeks) => runHoursCopy(source, weeks, true)}
          onClose={() => { setHoursCopyOpen(false); setHoursPlan(null); }}
        />
      )}

      {usualRun && (
        <HoursRunDialog
          open
          run={usualRun.run}
          busy={busy}
          title={usualRun.run.committed ? 'Usual hours booked' : `The usual hours for ${fmtDate(usualRun.date)}`}
          description={usualRun.run.committed
            ? 'What this booked, in the kitchen\u2019s local time.'
            : 'Straight from the standing weekly hours, trimmed to the landlord\u2019s agreement if it has to be. Nothing is booked until you confirm.'}
          confirmLabel="Book the usual hours"
          onConfirm={() => usualHours({ date: usualRun.date }, true)}
          onClose={() => setUsualRun(null)}
        />
      )}

      {impact && (
        <ImpactPreviewDialog
          open
          impact={impact.impact}
          title={impact.title}
          confirmLabel={impact.confirmLabel}
          busy={busy}
          onConfirm={confirmImpact}
          onCancel={() => setImpact(null)}
        />
      )}
    </div>
  );
};

export default Builder;
